import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  FilterBar,
  PageHeader,
  Select,
  cn,
} from "../../shared/ui";
import type { CalendarDetail, HolidayView } from "../../shared/types/api";
import { CalendarDrawer } from "./CalendarDrawer";
import { HolidayDrawer } from "./HolidayDrawer";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

/** Minutes are the stored unit (§41); hours exist only for reading. */
function asHours(minutes: number): string {
  if (minutes === 0) {
    return "—";
  }
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

/**
 * Working calendars and public holidays (§37, §38).
 *
 * <p>Capacity for the selected year is shown alongside, because a calendar's practical meaning is
 * the number of working hours it produces — and that only becomes visible once holidays are
 * subtracted from it.
 */
export function CalendarsPage() {
  const { can } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [addingHoliday, setAddingHoliday] = useState(false);
  // null while closed; a calendar to edit, or "new" to create one.
  const [calendarForm, setCalendarForm] = useState<"new" | CalendarDetail | null>(null);

  const calendars = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.get<CalendarDetail[]>("/api/v1/working-calendars"),
  });

  const activeId = selectedId ?? calendars.data?.[0]?.id ?? null;
  const active = calendars.data?.find((calendar) => calendar.id === activeId);

  const from = `${year}-01-01`;
  const to = `${year}-12-31`;

  const holidays = useQuery({
    queryKey: ["holidays", activeId, year],
    queryFn: () =>
      api.get<HolidayView[]>(`/api/v1/holidays?calendarId=${activeId}&from=${from}&to=${to}`),
    enabled: Boolean(activeId),
  });

  const capacity = useQuery({
    queryKey: ["capacity", activeId, year],
    queryFn: () =>
      api.get<{ workingDays: number; grossMinutes: number; availableMinutes: number }>(
        `/api/v1/working-calendars/${activeId}/capacity?from=${from}&to=${to}`,
      ),
    enabled: Boolean(activeId),
  });

  const removeHoliday = async (id: string) => {
    await api.del(`/api/v1/holidays/${id}`);
    void holidays.refetch();
    void capacity.refetch();
  };

  if (calendars.isPending) {
    return <LoadingState label="Loading calendars…" />;
  }
  if (calendars.isError) {
    return <ErrorState error={calendars.error} onRetry={() => void calendars.refetch()} />;
  }
  if (calendars.data.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No working calendars"
          message="A calendar defines which days are worked and for how long. One should have been created by the initial setup."
        />
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        title="Calendars & holidays"
        subtitle="Working patterns and non-working days. These decide everyone's expected hours."
      />

      <FilterBar actions={
          can("CALENDAR_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCalendarForm("new")}>
              New calendar
            </Button>
          )
        }>
          <div className="w-64">
            <Field label="Calendar">
              <Select
                value={activeId ?? ""}
                onChange={(event) => setSelectedId(event.target.value)}
              >
                {calendars.data.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name}
                    {calendar.defaultCalendar ? " (default)" : ""}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-32">
            <Field label="Year">
              <Select value={year} onChange={(event) => setYear(Number(event.target.value))}>
                {Array.from(
                  { length: 5 },
                  (_, index) => new Date().getFullYear() - 1 + index,
                ).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
      </FilterBar>

      {active && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="space-y-4">
            <Card>
              <CardHeader
                title="Working week"
                subtitle={`${asHours(active.weeklyMinutes)} a week · ${active.timeZone}`}
                action={
                  can("CALENDAR_MANAGE") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Pencil className="h-3.5 w-3.5" />}
                      onClick={() => setCalendarForm(active)}
                    >
                      Edit
                    </Button>
                  )
                }
              />
              <CardBody className="p-0">
                <ul>
                  {DAYS.map((day) => {
                    const minutes = active.workingMinutesByDay[day] ?? 0;
                    return (
                      <li
                        key={day}
                        className={cn(
                          "flex items-center justify-between border-b border-ink-100 px-5 py-2 text-sm last:border-0",
                          minutes === 0 && "text-ink-400",
                        )}
                      >
                        <span className="capitalize">{day.toLowerCase()}</span>
                        <span className="tnum font-medium">{asHours(minutes)}</span>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>

            {capacity.isSuccess && (
              <Card>
                <CardHeader
                  title={`${year} capacity`}
                  subtitle="Gross working time, less public holidays."
                />
                <CardBody className="space-y-2 text-sm">
                  <Row label="Working days" value={String(capacity.data.workingDays)} />
                  <Row label="Gross" value={asHours(capacity.data.grossMinutes)} />
                  <Row
                    label="Less holidays"
                    value={asHours(capacity.data.grossMinutes - capacity.data.availableMinutes)}
                  />
                  <div className="border-t border-ink-100 pt-2">
                    <Row label="Available" value={asHours(capacity.data.availableMinutes)} strong />
                  </div>
                  <p className="pt-1 text-xs text-ink-400">
                    A holiday on a non-working day subtracts nothing. Leave is subtracted in Phase
                    5, once it is integrated from HRMS.
                  </p>
                </CardBody>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader
              title={`Holidays in ${year}`}
              subtitle={
                holidays.isSuccess ? `${holidays.data.length} recorded` : undefined
              }
              action={
                can("CALENDAR_MANAGE") && (
                  <Button
                    variant="subtle"
                    size="sm"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => setAddingHoliday(true)}
                  >
                    Add holiday
                  </Button>
                )
              }
            />
            <CardBody className="p-0">
              {holidays.isPending && <LoadingState label="Loading holidays…" />}
              {holidays.isError && (
                <div className="p-5">
                  <ErrorState error={holidays.error} />
                </div>
              )}
              {holidays.isSuccess && holidays.data.length === 0 && (
                <EmptyState
                  title={`No holidays in ${year}`}
                  message="Everyone on this calendar is expected to work every working day."
                />
              )}
              {holidays.isSuccess && holidays.data.length > 0 && (
                <ul>
                  {holidays.data.map((holiday) => (
                    <li
                      key={holiday.id}
                      className="flex items-center justify-between gap-3 border-b border-ink-100 px-5 py-2.5 text-sm last:border-0"
                    >
                      <span className="tnum w-28 shrink-0 text-ink-500">{holiday.date}</span>
                      <span className="flex-1 text-ink-900">{holiday.name}</span>
                      {can("CALENDAR_MANAGE") && (
                        <button
                          type="button"
                          aria-label={`Remove ${holiday.name}`}
                          onClick={() => void removeHoliday(holiday.id)}
                          className="rounded-md p-1.5 text-ink-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      <CalendarDrawer
        calendar={calendarForm === "new" ? null : calendarForm}
        open={calendarForm !== null}
        onClose={() => setCalendarForm(null)}
        onSaved={() => {
          void calendars.refetch();
          void capacity.refetch();
        }}
      />

      {activeId && (
        <HolidayDrawer
          calendarId={activeId}
          open={addingHoliday}
          onClose={() => setAddingHoliday(false)}
          onSaved={() => {
            void holidays.refetch();
            void capacity.refetch();
          }}
        />
      )}
    </>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-500">{label}</span>
      <span className={cn("tnum", strong ? "font-semibold text-ink-900" : "text-ink-700")}>
        {value}
      </span>
    </div>
  );
}
